import { useEffect } from "react";

import * as Sentry from "@sentry/react";
import type { ServiceError } from "@shapr3d/frontend-lib";
import { isLoggedIn } from "@shapr3d/frontend-lib";
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { z } from "zod";

import { queryKeys } from "../config";
import { UMSrequestClient } from "../stores/requestClient";
import { SubscriptionPeriod, SubscriptionTier, SubscriptionType } from "../types";
import {
  type AccountData,
  type AccountResponseData,
  type Subscription,
  type UserError,
} from "./useAccountTypes";
import { usePaddleContext } from "./usePaddle";
import { TeamSubscriptionsQueryKey } from "./useTeams";
import type { TeamsSubscriptionResponseData } from "./useTeamsTypes";

export const accountQueryKey = [...queryKeys.authenticatedQueryKey, "account"];
export const paymentPlatformSentryTag = "paymentPlatform";

export const UserID = z.string().brand<"UserID">();
export type UserID = z.infer<typeof UserID>;

const POLL_INTERVAL_MS = 1000;

const accountResponseTransformer = ({ data }: { data: AccountResponseData }): AccountData => {
  const subscriptions = data.subscriptions.map(
    (subscription): Subscription => ({
      ...subscription,
      expiration: new Date(subscription.expiration),
      gracePeriodExpirationDate: subscription.gracePeriodExpirationDate
        ? new Date(subscription.gracePeriodExpirationDate)
        : undefined,
    })
  );

  const hasAppStoreSubscription = subscriptions.some(
    (subscription) => subscription.type === SubscriptionType.AppStore
  );
  const hasPaddleSubscription = subscriptions.some(
    (subscription) => subscription.type === SubscriptionType.Paddle
  );
  const hasChargebeeSubscription = subscriptions.some(
    (subscription) => subscription.type === SubscriptionType.Chargebee
  );
  const subscriptionInGracePeriod = subscriptions.find(
    (subscription) =>
      Boolean(subscription.gracePeriodExpirationDate) &&
      subscription.gracePeriodExpirationDate &&
      subscription.gracePeriodExpirationDate > subscription.expiration
  );

  const canPurchaseSubscription = () => {
    if (!isLoggedIn() || hasPaddleSubscription || hasChargebeeSubscription) {
      return false;
    }

    if (hasAppStoreSubscription) {
      const hasAppStoreWithAutoRenewal = data.subscriptions.some(
        (subscription) =>
          subscription.type === SubscriptionType.AppStore && subscription.autorenewal
      );
      return !hasAppStoreWithAutoRenewal;
    }

    return true;
  };

  let teamSubscriptions: Subscription[] = [];
  if (data.teamSubscription && data.teamSubscription.expirationDate) {
    const subscription: Subscription = {
      teamId: data.teamSubscription.id,
      type: SubscriptionType.Team,
      expiration: new Date(data.teamSubscription.expirationDate),
      autorenewal: false,
      isTrial: false,
      subscriptionPeriod: SubscriptionPeriod.Yearly,
      teamName: data.teamSubscription.name,
      tier: SubscriptionTier.Pro,
    };
    teamSubscriptions = [subscription];
  }

  const hasTeamSubscription = teamSubscriptions.length > 0;

  const techTrialAvailable =
    canPurchaseSubscription() &&
    data.subscriptions.length === 1 &&
    data.subscriptions.some((sub) => sub.type === SubscriptionType.AppStore) &&
    data.availablePlans.length > 0 &&
    !hasTeamSubscription;

  // removes teamSubscription property from the data object
  const { teamSubscription: _, ...dataWithoutTeamSubscription } = data;

  return {
    ...dataWithoutTeamSubscription,
    subscriptions: subscriptions.concat(teamSubscriptions),
    hasPaddleSubscription,
    hasChargebeeSubscription,
    hasAppStoreSubscription,
    subscriptionInGracePeriod,
    canPurchaseSubscription,
    techTrialAvailable,
  } satisfies AccountData;
};

export default function useAccount(
  { enabled } = { enabled: true }
): UseQueryResult<AccountData, ServiceError> {
  const { subscriptionIdToPollForGracePeriodUpdate, setSubscriptionIdToPollForGracePeriodUpdate } =
    usePaddleContext();

  const accountQuery = useQuery<
    AccountResponseData,
    ServiceError,
    AccountData,
    typeof accountQueryKey
  >({
    queryKey: [...accountQueryKey],
    queryFn: ({ signal }) =>
      UMSrequestClient.authenticatedRequest({
        method: "GET",
        url: "/account",
        abortSignal: signal,
      }).then((res) => {
        return res.json<AccountResponseData>();
      }),
    refetchInterval: (query) => {
      const data = query.state.data
        ? accountResponseTransformer({ data: query.state.data })
        : undefined;

      if (query.state.dataUpdateCount >= 20) return false;

      if (subscriptionIdToPollForGracePeriodUpdate !== undefined) {
        const subscriptionToPoll = data?.subscriptions.find(
          ({ id }) => id === subscriptionIdToPollForGracePeriodUpdate
        );
        if (subscriptionToPoll && subscriptionToPoll?.gracePeriodExpirationDate) {
          return POLL_INTERVAL_MS;
        }
        setSubscriptionIdToPollForGracePeriodUpdate(undefined);
      }
      return false;
    },
    staleTime: Infinity,
    enabled: isLoggedIn() && enabled,
    select: (data) => accountResponseTransformer({ data }),
  });

  useEffect(() => {
    if (accountQuery?.data?.subscriptions?.some((sub) => sub.type === SubscriptionType.Paddle)) {
      Sentry.setTag(paymentPlatformSentryTag, "paddle");
    }

    if (accountQuery?.data?.subscriptions?.some((sub) => sub.type === SubscriptionType.Chargebee)) {
      Sentry.setTag(paymentPlatformSentryTag, "chargebee");
    }
  }, [accountQuery.data]);

  return accountQuery;
}

export function useMutatePaddleAutoRenew({ teamID }: { teamID?: string }) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [...queryKeys.authenticatedQueryKey, "paddle-auto-renew"],
    mutationFn: ({
      paddleSubscriptionID,
      autoRenew,
    }: {
      paddleSubscriptionID: string;
      autoRenew: boolean;
    }) =>
      UMSrequestClient.authenticatedRequest({
        method: "PUT",
        url: `/subscriptions/${paddleSubscriptionID}/auto-renew`,
        body: {
          autoRenew,
        },
      }),
    onSuccess: (data, { paddleSubscriptionID, autoRenew }) => {
      queryClient.setQueryData<AccountResponseData | undefined>(accountQueryKey, (oldData) => {
        if (!oldData) return undefined;
        const newData = JSON.parse(JSON.stringify(oldData));
        const updatedSubscription = newData.subscriptions.find(
          ({ paddleId }: { paddleId: string }) => paddleId == paddleSubscriptionID
        );
        if (updatedSubscription) {
          updatedSubscription.autorenewal = autoRenew;
        }
        return newData;
      });

      if (teamID) {
        queryClient.setQueryData<TeamsSubscriptionResponseData | undefined>(
          [...TeamSubscriptionsQueryKey, teamID],
          (oldData) => {
            if (!oldData) {
              return undefined;
            }

            if (!oldData.individualSubscriptions) {
              return oldData;
            }

            const paddleSubscription = structuredClone(
              oldData.individualSubscriptions?.find((sub) => sub.paddleId === paddleSubscriptionID)
            );

            if (!paddleSubscription) {
              return oldData;
            }

            paddleSubscription.autoRenewal = autoRenew;
            const newSubscriptionsData = structuredClone(
              oldData.individualSubscriptions.filter((sub) => sub.paddleId !== paddleSubscriptionID)
            );
            newSubscriptionsData.push(paddleSubscription);
            return { ...oldData, individualSubscriptions: newSubscriptionsData };
          }
        );
      }
    },
  });
}

export function useMutateChargebeeAutoRenew({ teamID }: { teamID?: string }) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [...queryKeys.authenticatedQueryKey, "chargebee-auto-renew"],
    mutationFn: ({
      teamSubscriptionID,
      autoRenew,
    }: {
      teamSubscriptionID: string;
      autoRenew: boolean;
    }) =>
      UMSrequestClient.authenticatedRequest({
        method: "PUT",
        url: `/teams/subscriptions/${teamSubscriptionID}/auto-renew`,
        body: {
          autoRenew,
        },
      }),
    onSuccess: (data, { teamSubscriptionID, autoRenew }) => {
      queryClient.setQueryData<AccountResponseData | undefined>(accountQueryKey, (oldData) => {
        if (!oldData) return undefined;
        const newData: AccountResponseData = JSON.parse(JSON.stringify(oldData));
        const updatedSubscription = newData.subscriptions.find(
          (sub) => sub.teamSubscriptionId == teamSubscriptionID
        );

        if (updatedSubscription) {
          updatedSubscription.autorenewal = autoRenew;
        }

        if (updatedSubscription?.isOnlyCancelable) {
          newData.subscriptions = newData.subscriptions.filter(
            (sub) => sub.teamSubscriptionId !== teamSubscriptionID
          );
        }
        return newData;
      });

      if (teamID) {
        queryClient.setQueryData<TeamsSubscriptionResponseData | undefined>(
          [...TeamSubscriptionsQueryKey, teamID],
          (oldData) => {
            if (!oldData || !oldData.seatBasedTeamSubscription) {
              return oldData;
            }

            oldData.seatBasedTeamSubscription.autoRenewal = autoRenew;

            if (oldData.seatBasedTeamSubscription.gracePeriodExpirationDate) {
              oldData.seatBasedTeamSubscription = undefined;
            }

            return oldData;
          }
        );
      }
    },
  });
}

export function useCancelSubscription() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [...queryKeys.authenticatedQueryKey, "cancel"],
    mutationFn: ({ paddleSubscriptionID }: { paddleSubscriptionID: string }) =>
      UMSrequestClient.authenticatedRequest({
        method: "PUT",
        url: `/subscriptions/${paddleSubscriptionID}/cancel`,
      }),
    onSuccess: (data, { paddleSubscriptionID }) => {
      queryClient.setQueryData<AccountResponseData | undefined>(accountQueryKey, (oldData) => {
        if (!oldData) return undefined;
        const newData = JSON.parse(JSON.stringify(oldData));
        const updatedSubscription = newData.subscriptions.find(
          ({ paddleId }: { paddleId: string }) => paddleId == paddleSubscriptionID
        );
        if (updatedSubscription) {
          updatedSubscription.autorenewal = false;
        }
        return newData;
      });
    },
  });
}

export function useMutateEmailChangeConfirm() {
  const queryClient = useQueryClient();
  return useMutation<{ newEmail: string }, UserError | Error, string>({
    mutationKey: ["email-change-confirm"],
    mutationFn: (confirmationHash: string) =>
      UMSrequestClient.publicRequest({
        method: "POST",
        url: "/change-email/confirm",
        body: { confirmationHash },
      }).then((res) => {
        return res.json<{ newEmail: string }>();
      }),
    onMutate: () => {
      queryClient.cancelQueries({ queryKey: accountQueryKey });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: accountQueryKey });
    },
  });
}
